import { v4 as uuid4 } from 'uuid';
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { type Tab } from './ModulePageLayout';

export interface FavoriteStore {
    favorites: Tab[];
    addFavorite: (favoriteId: Omit<Tab, 'id'> & { id?: string }) => void;
    removeFavorite: (favoriteId: Tab['id']) => void;
    resetFavorites: () => void;
}

type FavoritesStoreHook = UseBoundStore<StoreApi<FavoriteStore>>;

const favoritesStoreRegistry = new Map<string, FavoritesStoreHook>();

const getFavoritesStore = (storageName = 'favorites-storage') => {
    if (!favoritesStoreRegistry.has(storageName)) {
        favoritesStoreRegistry.set(
            storageName,
            create<FavoriteStore>()(
                persist(
                    (set, get) => ({
                        resetFavorites: () => set({ ...get(), favorites: [] }),
                        favorites: [],
                        addFavorite: (favorite) => {
                            const favoriteId = favorite.id ?? uuid4();
                            set({
                                ...get(),
                                favorites: [...get().favorites, { ...favorite, id: favoriteId }]
                            });
                        },
                        removeFavorite: (favoriteId) => {
                            set({ ...get(), favorites: get().favorites.filter((t) => t.id !== favoriteId) });
                        }
                    }),
                    {
                        name: storageName,
                        storage: createJSONStorage(() => localStorage)
                    }
                )
            )
        );
    }

    return favoritesStoreRegistry.get(storageName)!;
};

export const useFavoritesStore = (storageName = 'favorites-storage') => getFavoritesStore(storageName);

const useFavorites = (storageName?: string) => useFavoritesStore(storageName)();

export default useFavorites;
